import { assertNotEmpty } from '@festina-life/core'
import cx from 'classnames'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import {
  Group,
  Panel,
  Separator,
  useDefaultLayout,
} from 'react-resizable-panels'

import { CircularSpinner } from '../../feedback/circularSpinner/CircularSpinner'
import { useDebounceCallback } from '../../hooks/useDebounceCallback'
import { useTranslate } from '../../hooks/useTranslate'
import { SearchResultPreview } from './SearchResultPreview'
import type { PreviewProps, SearchCategory, SearchResult } from './types'
import { usePreviewIndex } from './usePreviewIndex'

const SearchResultKeyboardNavigationKeys = ['ArrowUp', 'ArrowDown', 'Enter']

type Props<Value, Match, Category extends string> = {
  query: string
  category: SearchCategory<Value, Match, Category>
  onSelect: (
    value: SearchResult<Value, Match, Category>,
    previewIndex?: number,
  ) => void
  minQueryLength: number
  debounceMilliseconds: number
  isOpen: boolean
} & (
  | {
      isShowingPreview: true
      renderPreview: (props: PreviewProps<Value, Match, Category>) => ReactNode
    }
  | {
      isShowingPreview: false
      renderPreview?: never
    }
)

export const SearchResults = <Value, Match, Category extends string>({
  query,
  category,
  minQueryLength,
  isOpen,
  debounceMilliseconds,
  onSelect,
  isShowingPreview,
  ...other
}: Props<Value, Match, Category>) => {
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    groupId: 'searchPanels',
    storage: localStorage,
  })
  const translate = useTranslate()
  const [results, setResults] = useState<
    SearchResult<Value, Match, Category>[]
  >([])
  const [activeResultIndex, setActiveResultIndex] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const debouncedSearch = useDebounceCallback(
    async (search: string) => {
      setIsSearching(true)

      const result = await category.search(search)

      setResults(result)
      setIsSearching(false)
      setActiveResultIndex(0)
    },
    debounceMilliseconds,
    [category],
  )

  const activeResult = results.at(activeResultIndex)
  const matchesCount = (activeResult?.matches ?? []).length
  const {
    previous,
    index: previewIndex,
    next,
    reset,
  } = usePreviewIndex({ count: matchesCount, isActive: isOpen })

  useEffect(
    function resetPreviewIndexOnDifferentResult() {
      reset()
    },
    [activeResult, reset],
  )

  const handleKeyPressed = useCallback(
    (event: KeyboardEvent) => {
      if (
        SearchResultKeyboardNavigationKeys.includes(event.key) &&
        !isSearching
      ) {
        event.stopPropagation()
        event.preventDefault()

        if (event.key === 'Enter') {
          const activeResult = results.at(activeResultIndex)
          assertNotEmpty(activeResult)
          onSelect(activeResult, previewIndex)
        } else if (event.key === 'ArrowDown') {
          setActiveResultIndex((activeResultIndex + 1) % results.length)
        } else if (event.key === 'ArrowUp') {
          const nextIndex = activeResultIndex - 1
          setActiveResultIndex(nextIndex < 0 ? results.length - 1 : nextIndex)
        }
      }
    },
    [activeResultIndex, isSearching, onSelect, previewIndex, results],
  )

  const handleActiveElementRef = (element: HTMLLIElement | null) => {
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }

  useEffect(
    function searchOnQueryChange() {
      if (query.length >= minQueryLength) {
        debouncedSearch.cancel()
        setResults([])

        debouncedSearch?.(query)
      } else {
        setResults([])
      }
    },
    [debouncedSearch, minQueryLength, query],
  )

  useEffect(
    function listenToShiftKeyPressesWhenClosed() {
      if (!isOpen) {
        return
      }

      document.addEventListener('keydown', handleKeyPressed)

      return () => {
        document.removeEventListener('keydown', handleKeyPressed)
      }
    },
    [handleKeyPressed, isOpen],
  )

  return (
    <Group
      id="search"
      className="min-h-80 grow"
      orientation="vertical"
      defaultLayout={defaultLayout}
      onLayoutChanged={onLayoutChanged}
    >
      <Panel id="searchResultsList" minSize="10%" defaultSize="25%">
        <div
          className={cx(
            'flex size-full flex-col items-center overflow-y-auto',
            {
              'justify-center': isSearching,
            },
          )}
        >
          <CircularSpinner
            className={cx('size-3', { invisible: !isSearching })}
          />
          <ol className="w-full">
            {results.map((result, index) => (
              <li
                /* oxlint-disable-next-line react/no-array-index-key */
                key={`${result.value.id}-${index}`}
                className={cx('w-full cursor-pointer', {
                  'bg-primary-300': activeResultIndex === index,
                  'hover:bg-primary-100 dark:hover:bg-primary-700':
                    activeResultIndex !== index,
                })}
                ref={
                  index === activeResultIndex
                    ? handleActiveElementRef
                    : undefined
                }
                onClick={() => onSelect(result, previewIndex)}
              >
                {category.render(result)}
              </li>
            ))}
          </ol>
          {!isSearching && !results.length && (
            <div className="w-full opacity-50">{translate('notFound')}</div>
          )}
        </div>
      </Panel>
      {isShowingPreview &&
        other.renderPreview &&
        activeResult &&
        !!matchesCount && (
          <>
            <Separator
              id="resizePreviewBar"
              className="my-2 h-1.5 dark:bg-slate-700 bg-slate-300"
            />
            <Panel id="searchPreview" className="relative" defaultSize="75%">
              <SearchResultPreview
                key={activeResultIndex}
                onNext={next}
                onPrevious={previous}
                previewIndex={previewIndex}
                count={matchesCount}
                isActive={isOpen}
              >
                {other.renderPreview({
                  value: activeResult,
                  matchIndex: previewIndex,
                  categoryKey: category.key,
                })}
              </SearchResultPreview>
            </Panel>
          </>
        )}
    </Group>
  )
}
